export interface GTMConfig {
  containerId?: string;
}

interface UserData {
  em?: string;
  fn?: string;
  ln?: string;
  external_id?: string;
  client_ip_address?: string;
  client_user_agent?: string;
  fbp?: string;
  fbc?: string;
}

interface GTMEventData {
  event: string;
  event_id?: string;
  user_data?: UserData;
  [key: string]: any;
}

const warn = (...args: unknown[]) => {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  console.warn(...args);
};

// Utility function to generate UUID v4
function uuidv4(): string {
  return ([1e7] as any + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c: any) =>
    (c ^ ((crypto.getRandomValues(new Uint8Array(1))[0] & 15) >> (c / 4))).toString(16)
  );
}

// Utility function to get cookie value by name
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}

// Utility function to hash data using SHA-256
async function hashData(data: string): Promise<string> {
  if (!data) return '';

  // Normalize: lowercase and trim
  const normalized = data.toLowerCase().trim();

  // Hash using SHA-256
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(normalized);
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);

  // Convert to hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

  return hashHex;
}

class GTM {
  CONTAINER_ID?: string;

  initialized = false;

  configure(config: GTMConfig) {
    if (!config.containerId) {
      warn('GTM requires a GTM ID to be loaded.');
      return;
    }

    this.CONTAINER_ID = config.containerId;
  }

  initialize(config: GTMConfig) {
    if (this.initialized) {
      warn('GTM can only be initialized once.');
      return;
    }

    // Maybe you want to load events from server side (in NextJS apps for example),
    // those can be queued.
    // SSR queued events can be loaded in the initialize script.
    // For the moment we do not implement it, but in future we might add it.

    if (!document) {
      warn('GTM can be initialized only on client side.');
      return;
    }

    this.configure(config);

    if (!this.CONTAINER_ID) {
      return;
    }

    const script = document.createElement('script');
    const noscript = document.createElement('noscript');

    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${this.CONTAINER_ID}');
    `;
    noscript.innerHTML = `
      <iframe src="https://www.googletagmanager.com/ns.html?id=${this.CONTAINER_ID}" height="0" width="0" style="display:none;visibility:hidden"></iframe>
    `;

    document.head.insertBefore(script, document.head.childNodes[0]);
    document.body.insertBefore(noscript, document.body.childNodes[0]);
  }

  // eslint-disable-next-line class-methods-use-this
  push(...args: any[]) {
    if (!window) {
      warn('GTM push works only on client side.');
      return;
    }

    if (!(window as any).dataLayer) {
      (window as any).dataLayer = [];
    }

    (window as any).dataLayer.push(...args);
  }

  // New method for dual tracking (client GTM + backend API)
  async pushWithBackend(eventData: GTMEventData, userData?: {
    email?: string;
    firstName?: string;
    lastName?: string;
    ud_id?: string;
  }) {
    if (!window) {
      warn('GTM push works only on client side.');
      return;
    }

    try {
      // Use provided event_id or generate unique one
      const event_id = eventData.event_id || uuidv4();

      // Collect user data if provided
      let user_data: UserData | undefined;
      if (userData) {
        user_data = {
          em: userData.email ? await hashData(userData.email) : undefined,
          fn: userData.firstName ? await hashData(userData.firstName) : undefined,
          ln: userData.lastName ? await hashData(userData.lastName) : undefined,
          external_id: userData.ud_id || undefined,
          client_user_agent: navigator?.userAgent || undefined,
          fbp: getCookie('_fbp') || undefined,
          fbc: getCookie('_fbc') || undefined,
        };
      }

      // Create enriched event data
      const enrichedEventData = {
        ...eventData,
        event_id,
        ...(user_data && { user_data }),
      };

      // 1. Push to client-side GTM dataLayer
      this.push(enrichedEventData);

      // 2. Send to backend API
      try {
        const apiUrl = process.env.REACT_APP_SNUG_API_URL;
        await fetch(`${apiUrl}/v3/gtm-events/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(enrichedEventData),
        });
      } catch (error) {
        // Log error but don't fail the client-side tracking
        console.error('Failed to send GTM event to backend:', error);
      }
    } catch (error) {
      console.error('Error in pushWithBackend:', error);
      // Fallback to regular push
      this.push(eventData);
    }
  }
}

// Singleton
export const gtm = new GTM();
